import { PROSPECT_PREFIX, CUSTOMER_PREFIX } from './constants';
import {
  conversationIndicatorStatus,
  effectiveConversationStatus,
  normalizeConversationStatus,
} from '../../utils/texting';
import type {
  Message,
  Conversation,
  ConversationStatus,
  MessageStatus,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress/TextingTab/TextingTab.types'; // eslint-disable-line max-len
import type { HouseholdMember } from './types';

export function prospectToHouseholdMember(
  prospectDto: Object,
): HouseholdMember {
  return {
    id: `${PROSPECT_PREFIX}${prospectDto.id}`,
    name: `${prospectDto.firstName}${
      prospectDto.lastName ? ` ${prospectDto.lastName}` : ''
    }`,
    phone: prospectDto.phoneNumber,
    contactId: prospectDto.contactId,
  };
}

export function customerToHouseholdMember(
  customerDto: Object,
): HouseholdMember {
  return {
    id: `${CUSTOMER_PREFIX}${customerDto.id}`,
    name: `${customerDto.firstName}${
      customerDto.lastName ? ` ${customerDto.lastName}` : ''
    }`,
    phone: customerDto.phoneNumber,
    contactId: customerDto.contactId,
  };
}

export function conversationDtoToConversation(
  conversationDto: Object,
  householdMember: HouseholdMember,
  leslieEnabled?: boolean,
): Conversation {
  return {
    id: conversationDto['_id'],
    recipients: [{ ...householdMember }],
    lastReceivedMessage: conversationDto.lastReceivedMessage
      ? {
          content: conversationDto.lastReceivedMessage.content,
          dateDelivered: conversationDto.lastReceivedMessage.updatedAt,
        }
      : null,
    // Stays the WORKFLOW status, read through the flag. The resolve banner reflects it and the
    // resolve toggle and the mark-as-read write both act on it, so overloading it with Leslie's
    // flag would make the banner disagree with the action behind it.
    status: conversationStatusToStatus(
      effectiveConversationStatus(conversationDto),
    ),
    // The indicator, carried separately and gated on the flag.
    needsHelp:
      conversationIndicatorStatus(conversationDto, leslieEnabled) ===
      'NEEDS_HELP',
  };
}

export function conversationStatusToStatus(
  conversationStatus: string,
): ConversationStatus {
  const status = normalizeConversationStatus(conversationStatus);
  if (status === 'INVALID_PHONE_NUMBER') return 'invalid-phone';
  if (status === 'NEW') return 'new-message';
  if (status === 'UNRESOLVED') return 'unresolved';
  return 'resolved';
}

export function messageDtoToMessage({
  messageDto,
  conversation,
  selectedProperty,
  user,
  includeUser,
  includeAttachments,
  leslieEnabled,
}: {
  messageDto: Object,
  conversation: Conversation,
  selectedProperty: Object,
  includeUser?: boolean,
  user?: Object,
  includeAttachments: boolean,
  leslieEnabled?: boolean,
}): Message {
  const isAutomated = messageDto.isAutomated === true;
  const senderType =
    messageDto.type === 'OUTBOUND' ? 'property' : 'household-member';
  const recipient =
    senderType === 'household-member' ? conversation.recipients[0] : undefined;
  return {
    id: messageDto['_id'],
    content: messageDto.content,
    sender: recipient?.name ?? selectedProperty.name,
    senderType,
    // Only ever true on the message Leslie flagged, so the conversation shows which one caused
    // the hand-off. Gated on the flag: with `leslie` off nothing this story adds is displayed.
    needsHelp: Boolean(leslieEnabled) && messageDto.needsHelp === true,
    dateDelivered: messageDto.updatedAt,
    deliveryFailureReason:
      messageDto.messageStatus === 'INVALID_PHONE_NUMBER'
        ? 'invalid-phone'
        : undefined,
    // Staff need to see that a reminder to an opted-out recipient never went.
    status:
      isAutomated && messageDto.messageStatus !== 'BLOCKED_OPT_OUT'
        ? 'automated'
        : messageStatusToStatus(messageDto.messageStatus),
    isPhoneNumberChangeMsg:
      messageDto.content.startsWith('Phone number has changed from ') &&
      messageDto.type === 'INTERNAL',
    user: isAutomated
      ? null
      : includeUser && user
      ? `${user.firstName} ${user.lastName}`
      : null,
    attachments: includeAttachments
      ? messageDto.attachments?.map((a) => ({
          type: 'image',
          url: a.url,
        }))
      : undefined,
  };
}

export function messageStatusToStatus(messageStatus: string): MessageStatus {
  if (messageStatus === 'DELIVERED' || messageStatus === 'RECEIVED')
    return 'delivered';
  if (messageStatus === 'UNDELIVERED') return 'undelivered';
  if (messageStatus === 'FAILED') return 'failed';
  if (messageStatus === 'BLOCKED_OPT_OUT') return 'failed';
  if (messageStatus === 'INVALID_PHONE_NUMBER') return 'invalid-phone';
  if (messageStatus === 'MASS_COMMUNICATION') return 'mass-communication';
  if (messageStatus === 'AUTOMATED') return 'automated';
  return 'processing';
}
